import { useEffect, useMemo, useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import {
  channelsForPersona,
  dmsForPersona,
  peoplePool,
  selfPersonIdFromSession,
} from '@/utils/commsHelpers';
import ChannelList from '@/components/comms/ChannelList';
import DmList from '@/components/comms/DmList';
import MessageRow from '@/components/comms/MessageRow';
import MessageComposer from '@/components/comms/MessageComposer';
import ThreadPane from '@/components/comms/ThreadPane';
import PersonPicker from '@/components/comms/PersonPicker';
import CreateChannelModal from '@/components/comms/CreateChannelModal';
import Avatar from '@/components/comms/Avatar';

type Active =
  | { kind: 'channel'; id: string }
  | { kind: 'dm'; id: string };

export default function InternalComms() {
  const channelsMap = useBrainStore((s) => s.internalComms);
  const dmsMap = useBrainStore((s) => s.internalDmThreads);
  const agents = useBrainStore((s) => s.agents);
  const artists = useBrainStore((s) => s.artists);
  const sendChannelMessage = useBrainStore((s) => s.sendChannelMessage);
  const sendDmMessage = useBrainStore((s) => s.sendDmMessage);
  const openOrCreateDm = useBrainStore((s) => s.openOrCreateDm);
  const createChannel = useBrainStore((s) => s.createChannel);
  const addReaction = useBrainStore((s) => s.addReaction);

  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const artistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const lastReadByChannel = useSessionStore((s) => s.lastReadByChannel);
  const markCommsRead = useSessionStore((s) => s.markCommsRead);

  const clockIso = useSimulationStore((s) => s.clockIso);

  const selfId = selfPersonIdFromSession({ persona, agentId, artistId, apprenticeId });
  const people = useMemo(() => peoplePool(agents, artists), [agents, artists]);
  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);

  const visibleChannels = useMemo(
    () => channelsForPersona(persona, agentId, channelsMap),
    [persona, agentId, channelsMap],
  );
  const visibleDms = useMemo(() => dmsForPersona(selfId, dmsMap), [selfId, dmsMap]);

  const [active, setActive] = useState<Active | null>(() => {
    // Default to studio-ops channel for the persona's primary studio if available,
    // else the first visible channel.
    const ops = visibleChannels.find((c) => c.scope === 'studio-ops');
    const first = ops ?? visibleChannels[0];
    return first ? { kind: 'channel', id: first.id } : null;
  });
  const [threadParentId, setThreadParentId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [channelModalOpen, setChannelModalOpen] = useState(false);

  // Resolve the active container.
  const activeChannel = active?.kind === 'channel' ? channelsMap[active.id] : undefined;
  const activeDm = active?.kind === 'dm' ? dmsMap[active.id] : undefined;
  const activeMessages = useMemo(
    () =>
      (activeChannel?.messages ?? activeDm?.messages ?? []) as ReturnType<
        typeof channelsForPersona
      >[number]['messages'],
    [activeChannel, activeDm],
  );

  // Mark the active container read whenever it changes or new messages arrive.
  useEffect(() => {
    if (!active) return;
    markCommsRead(active.id, clockIso);
  }, [active, activeMessages.length, markCommsRead, clockIso]);

  // Group thread replies by parent for the affordance count.
  const replyCountByParent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of activeMessages) {
      if (m.parentMessageId) {
        counts.set(m.parentMessageId, (counts.get(m.parentMessageId) ?? 0) + 1);
      }
    }
    return counts;
  }, [activeMessages]);

  const topLevelMessages = useMemo(
    () => activeMessages.filter((m) => !m.parentMessageId),
    [activeMessages],
  );

  const threadParent = useMemo(() => {
    if (!threadParentId) return undefined;
    return activeMessages.find((m) => m.id === threadParentId);
  }, [activeMessages, threadParentId]);
  const threadReplies = useMemo(() => {
    if (!threadParentId) return [];
    return activeMessages.filter((m) => m.parentMessageId === threadParentId);
  }, [activeMessages, threadParentId]);

  const onSendActive = (body: string, mentions: string[]) => {
    if (!active || !selfId) return;
    if (active.kind === 'channel') {
      sendChannelMessage(active.id, body, {
        authorId: selfId,
        mentions,
        timestamp: clockIso,
      });
    } else {
      sendDmMessage(active.id, body, {
        authorId: selfId,
        mentions,
        timestamp: clockIso,
      });
    }
  };

  const onSendThread = (body: string, mentions: string[]) => {
    if (!active || !selfId || !threadParentId) return;
    if (active.kind === 'channel') {
      sendChannelMessage(active.id, body, {
        authorId: selfId,
        parentMessageId: threadParentId,
        mentions,
        timestamp: clockIso,
      });
    }
    // Thread replies on DMs aren't supported in this MVP — DMs flatten.
  };

  const onReact = (messageId: string, emoji: string) => {
    if (!active || !selfId) return;
    addReaction(
      { kind: active.kind, containerId: active.id },
      messageId,
      emoji,
      selfId,
    );
  };

  const onPickPerson = (personId: string) => {
    if (!selfId) return;
    const threadId = openOrCreateDm([selfId, personId], selfId, clockIso);
    setActive({ kind: 'dm', id: threadId });
    setPickerOpen(false);
  };

  const onCreateChannel = (name: string, memberIds: string[]) => {
    if (!selfId) return;
    const id = createChannel(name, memberIds, selfId);
    setActive({ kind: 'channel', id });
    setThreadParentId(null);
    setChannelModalOpen(false);
  };

  return (
    <section className="flex h-full min-h-0 overflow-hidden" data-testid="internal-comms">
      {/* Left rail · channels + DMs */}
      <aside className="w-60 shrink-0 border-r border-gold-dim/15 bg-ink-deep flex flex-col">
        <div className="px-3 py-3 border-b border-gold-dim/15">
          <p className="label-small-caps text-gold-bright">Comms</p>
          <p className="font-serif text-[11px] text-cream-soft mt-0.5">
            Channels + direct messages.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 flex items-center justify-between mb-1">
            <p className="label-small-caps text-cream-faint">Channels</p>
            <button
              type="button"
              onClick={() => setChannelModalOpen(true)}
              className="btn-quiet p-1"
              aria-label="Create channel"
              data-testid="comms-new-channel"
              title="Create channel"
            >
              <Plus size={11} />
            </button>
          </div>
          <ChannelList
            channels={visibleChannels}
            activeId={active?.kind === 'channel' ? active.id : null}
            onPick={(id) => {
              setActive({ kind: 'channel', id });
              setThreadParentId(null);
            }}
            lastReadByChannel={lastReadByChannel}
            selfPersonId={selfId}
          />
          <div className="px-3 mt-4 flex items-center justify-between">
            <p className="label-small-caps text-cream-faint">Direct messages</p>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="btn-quiet p-1"
              aria-label="New direct message"
              data-testid="comms-new-dm"
              title="New direct message"
            >
              <Plus size={11} />
            </button>
          </div>
          <DmList
            threads={visibleDms}
            activeId={active?.kind === 'dm' ? active.id : null}
            onPick={(id) => {
              setActive({ kind: 'dm', id });
              setThreadParentId(null);
            }}
            lastReadByChannel={lastReadByChannel}
            selfPersonId={selfId}
            peopleById={peopleById}
          />
        </div>
      </aside>

      {/* Centre · messages */}
      <section className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className="px-5 py-3 border-b border-gold-dim/15">
          {activeChannel ? (
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-inter text-lg text-cream">{activeChannel.name}</h2>
              <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                <Users size={10} className="inline mr-1" />
                {activeChannel.memberIds.length} members · {activeChannel.scope.replace('-', ' ')}
              </span>
              {activeChannel.description ? (
                <span className="font-serif text-cream-soft text-xs">
                  {activeChannel.description}
                </span>
              ) : null}
            </div>
          ) : activeDm ? (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex -space-x-2">
                {activeDm.participantIds
                  .filter((id) => id !== selfId)
                  .slice(0, 3)
                  .map((id) => (
                    <Avatar key={id} person={peopleById.get(id)} size="md" />
                  ))}
              </div>
              <h2 className="font-inter text-lg text-cream">
                {activeDm.participantIds
                  .filter((id) => id !== selfId)
                  .map((id) => peopleById.get(id)?.displayName ?? id)
                  .join(', ')}
              </h2>
              <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                Direct message
              </span>
            </div>
          ) : (
            <p className="font-serif text-cream-soft">
              Pick a channel or DM to start.
            </p>
          )}
        </header>
        <ol
          className="flex-1 min-h-0 overflow-y-auto py-2"
          data-testid="comms-message-stream"
        >
          {topLevelMessages.length === 0 ? (
            <li className="px-5 py-4 font-serif text-cream-soft text-sm">
              No messages here yet.
            </li>
          ) : null}
          {topLevelMessages.map((m) => (
            <li key={m.id}>
              <MessageRow
                message={m}
                peopleById={peopleById}
                threadReplyCount={replyCountByParent.get(m.id) ?? 0}
                onOpenThread={(id) => setThreadParentId(id)}
                onReact={onReact}
                selfPersonId={selfId}
                showThreadAffordance={active?.kind === 'channel'}
              />
            </li>
          ))}
        </ol>
        {active ? (
          <MessageComposer
            onSend={onSendActive}
            people={people}
            placeholder={
              activeChannel ? `Message ${activeChannel.name}` : 'Send a direct message…'
            }
          />
        ) : null}
      </section>

      {/* Right · thread pane (channels only) */}
      {threadParent && active?.kind === 'channel' ? (
        <ThreadPane
          parent={threadParent}
          replies={threadReplies}
          peopleById={peopleById}
          people={people}
          onClose={() => setThreadParentId(null)}
          onReply={onSendThread}
          onReact={onReact}
          selfPersonId={selfId}
        />
      ) : null}

      <PersonPicker
        open={pickerOpen}
        people={people}
        excludeIds={selfId ? [selfId] : []}
        onClose={() => setPickerOpen(false)}
        onPick={onPickPerson}
      />

      <CreateChannelModal
        open={channelModalOpen}
        people={people}
        excludeIds={selfId ? [selfId] : []}
        onClose={() => setChannelModalOpen(false)}
        onCreate={onCreateChannel}
      />
    </section>
  );
}
